import { useEffect, useMemo, useRef, useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import { useNotBilledDecision } from '@/hooks/useNotBilledDecision'
import { FreeTouchUpDialog } from '@/components/features/invoicing/FreeTouchUpDialog'
import { PERMISSIONS } from '@/lib/permissions'
import { DisabledHint } from '@/components/ui/disabled-hint'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  Check,
  Copy,
  FileText,
  MapPin,
  Minus,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'

import { TopBar } from '@/components/layouts/TopBar'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { useAuth } from '@/lib/auth-context'
import { cn } from '@/lib/utils'
import { ClientOrderPicker } from '@/components/features/planning/ClientOrderPicker'
import { PlanningActivationCard } from '@/components/features/planning/PlanningActivationCard'
import { OpenInPlanningLink } from '@/components/features/planning/OpenInPlanningLink'
import { selectPlanningSectionState } from '@/components/features/planning/planningSectionState'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { formatCurrency, formatDate, formatRelativeDate } from '@/lib/format'
import { Input } from '@/components/ui/input'
import { showErrorToast, showInfoToast, showSuccessToast, showWarningToast } from '@/lib/toast'
import { useProjectDetailsV2 } from '@/hooks/useProjectDetailsV2'
import { ProjectDocumentsSection } from '@/components/features/ProjectDocumentsSection'
import { archiveProjectV2, cancelSaleV2, removeQuoteFromSaleV2, updateProjectNameV2 } from '@/lib/api/v2/projects'
import { RemoveQuoteFromSaleDialog } from '@/components/features/project-detail/lifecycle/RemoveQuoteFromSaleDialog'
import { ProjectActionsMenu } from '@/components/features/project-detail/lifecycle/ProjectActionsMenu'
import { CancelSaleDialog } from '@/components/features/project-detail/lifecycle/CancelSaleDialog'
import { DeleteProjectDialog } from '@/components/features/project-detail/lifecycle/DeleteProjectDialog'
import { useProjectQuotesV2 } from '@/hooks/useProjectQuotesV2'
import {
  acceptQuoteVersionV2,
  createProjectQuoteDraftV2,
  declineQuoteVersionV2,
  deleteQuoteVersionV2,
  duplicateQuoteVersionV2,
  getProjectQuotesV2,
  getProjectQuoteVersionPdfUrlV2,
} from '@/components/features/project-detail/quotes/quotes-v2.api'
import type { QuoteV2, QuoteVersionV2 } from '@/components/features/project-detail/quotes/types'
import { QuoteAcceptVersionDialog } from '@/components/features/project-detail/quotes/QuoteAcceptVersionDialog'
import { WorkOrderSelectionDialog } from '@/components/features/project-detail/quotes/WorkOrderSelectionDialog'
import { ProjectLanguageControl } from '@/components/features/project-detail/ProjectLanguageControl'
import { InvoiceStatusBadge } from '@/components/features/invoicing/InvoiceStatusBadge'
import { useProjectInvoices } from '@/hooks/useProjectInvoices'
import { useProjectSalesVatsV2 } from '@/hooks/useProjectSalesVatsV2'
import { deleteProjectInvoice, getProjectExecutionDetails, getProjectInvoicePdfUrl, upsertProjectExecutionDetails } from '@/lib/api/projects'
import { InvoiceRowActions } from '@/components/features/project-detail/invoices/InvoiceRowActions'
import { DeleteInvoiceDialog } from '@/components/features/project-detail/invoices/DeleteInvoiceDialog'
import type { FloorKind, ProjectExecutionDetailsDto, ProjectInvoice } from '@/types'
import { ApiError } from '@/lib/api/client'
// NOTE: `config` import removed with the temporarily-hidden work-order button (2026-07-07).
// Re-add `import { config } from '@/lib/config'` when restoring handleGenerateWorkOrderPdf.
import { hasActiveFinalInvoice, hasNothingLeftToInvoice, resolveInvoicingSummaryState } from '@/lib/invoiceGating'
import { getInvoiceTypeLabel } from '@/lib/invoiceLabels'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DirectSaleOrderSection } from '@/components/features/project-detail/DirectSaleOrderSection'
import { WinnerImportButton } from '@/components/features/winner-import/WinnerImportButton'
import { WinnerSourceFileButton } from '@/components/features/winner-import/WinnerSourceFileButton'
import { MODULES, useModules } from '@/hooks/useModules'

type QuoteGroup = {
  id: string
  reference: string
  title: string
  versionsCount: number
  acceptedTotalWithTax: number | null
  draftVersions: QuoteVersionV2[]
  generatedVersions: QuoteVersionV2[]
}

function getQuoteStatusBadge(version: QuoteVersionV2) {
  const isAccepted = Boolean(version.acceptedAt) || version.status === 'accepted'
  const isSuperseded = version.isSuperseded === true || version.status === 'superseded'

  if (isAccepted && isSuperseded) {
    return (
      <span className="inline-flex items-center gap-2">
        <Badge className="rounded-md bg-green-100 text-green-700 border-0 line-through">Accepté</Badge>
        <Badge className="rounded-md bg-slate-100 text-slate-700 border-0">Remplacé</Badge>
      </span>
    )
  }

  if (isAccepted) {
    return <Badge className="rounded-md bg-green-100 text-green-700 border-0">Accepté</Badge>
  }

  if (version.status === 'declined') {
    return <Badge className="rounded-md bg-red-100 text-red-700 border-0">Refusé</Badge>
  }

  if (isSuperseded) {
    return <Badge className="rounded-md bg-slate-100 text-slate-700 border-0">Remplacé</Badge>
  }

  switch (version.status) {
    case 'sent':
      return <Badge className="rounded-md bg-sky-100 text-sky-700 border-0">Envoyé</Badge>
    case 'void':
      return <Badge className="rounded-md bg-red-100 text-red-700 border-0">Annulé</Badge>
    case 'generated':
    default:
      return <Badge className="rounded-md bg-slate-100 text-slate-700 border-0">Généré</Badge>
  }
}

function isAcceptedQuoteVersion(version: QuoteVersionV2) {
  return Boolean(version.acceptedAt) || version.status === 'accepted'
}

function isDeclinedQuoteVersion(version: QuoteVersionV2) {
  return version.status === 'declined'
}

function isSupersededQuoteVersion(version: QuoteVersionV2) {
  return version.isSuperseded === true || version.status === 'superseded'
}

function canAcceptIssuedQuoteVersion(version: QuoteVersionV2) {
  return !isAcceptedQuoteVersion(version) && !isDeclinedQuoteVersion(version) && !isSupersededQuoteVersion(version)
}

function canDeclineIssuedQuoteVersion(version: QuoteVersionV2) {
  return !isAcceptedQuoteVersion(version) && !isDeclinedQuoteVersion(version) && !isSupersededQuoteVersion(version)
}

function getInvoiceStatusBadgeV2(status: string | null | undefined) {
  if (status === 'sent') return <Badge className="rounded-md bg-sky-100 text-sky-700 border-0">Envoyée</Badge>
  if (status === 'paid') return <Badge className="rounded-md bg-green-100 text-green-700 border-0">Payée</Badge>
  return <Badge className="rounded-md bg-slate-100 text-slate-700 border-0">Générée</Badge>
}

/**
 * SOLY-ORDER-002 — feature flag: the quote-level « Retirer de la vente » gesture is hidden for now.
 * Decision (Thierry, 2026-07-22): only the project-level « Supprimer la vente » (header kebab) is
 * exposed. We keep the quote-level removal fully wired (dialog + API + BE) and will re-enable it by
 * flipping this to `true` if customers ask for per-quote removal. Do not delete the underlying code.
 */
const SHOW_QUOTE_REMOVE_FROM_SALE = false

export default function ProjectsIdPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { hasModule } = useModules()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { data: projectDetails, isLoading: projectLoading, refetch: refetchProjectDetails } =
    useProjectDetailsV2(id)

  const [isEditingName, setIsEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)

  const [isCreateQuoteConfirmOpen, setIsCreateQuoteConfirmOpen] = useState(false)
  const [complementaryQuoteName, setComplementaryQuoteName] = useState('')
  const [isDuplicateDialogOpen, setIsDuplicateDialogOpen] = useState(false)
  const [isDuplicatingVersion, setIsDuplicatingVersion] = useState(false)
  const [duplicateTarget, setDuplicateTarget] = useState<{
    quoteId: string
    versionId: string
    versionNumber: number
    documentNumber?: string | null
    versionName?: string | null
  } | null>(null)
  const [duplicateDraftName, setDuplicateDraftName] = useState('')

  const [isAcceptOrderOpen, setIsAcceptOrderOpen] = useState(false)
  const [acceptOrderQuoteId, setAcceptOrderQuoteId] = useState<string | null>(null)
  const [acceptOrderVersionId, setAcceptOrderVersionId] = useState<string | null>(null)
  const [acceptOrderVersionNumber, setAcceptOrderVersionNumber] = useState<number | null>(null)
  const [acceptOrderVersionName, setAcceptOrderVersionName] = useState<string | null>(null)

  const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false)
  const [isDecliningVersion, setIsDecliningVersion] = useState(false)
  const [declineTarget, setDeclineTarget] = useState<{
    quoteId: string
    versionId: string
    versionNumber: number
    versionName?: string | null
  } | null>(null)

  const [isDeleteDraftDialogOpen, setIsDeleteDraftDialogOpen] = useState(false)
  const [isDeletingDraft, setIsDeletingDraft] = useState(false)
  const [deleteDraftTarget, setDeleteDraftTarget] = useState<{
    quoteId: string
    versionId: string
    versionNumber: number
    versionName?: string | null
  } | null>(null)

  const [isWorkOrderDialogOpen, setIsWorkOrderDialogOpen] = useState(false)

  const {
    data: quotesResponse,
    isLoading: isQuotesLoading,
    error: quotesError,
    refetch: refetchQuotes,
  } = useProjectQuotesV2(id)

  const [isCreatingComplementaryQuote, setIsCreatingComplementaryQuote] = useState(false)

  const {
    data: invoicesData,
    isLoading: isInvoicesLoading,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useProjectInvoices(id)

  // SOLY-INV-001 — delete the last invoice
  const [deleteInvoiceTarget, setDeleteInvoiceTarget] = useState<ProjectInvoice | null>(null)
  const [isDeletingInvoice, setIsDeletingInvoice] = useState(false)

  const handleConfirmDeleteInvoice = async () => {
    if (!id || !deleteInvoiceTarget) return
    const target = deleteInvoiceTarget
    try {
      setIsDeletingInvoice(true)
      // Same reason as the quote acceptance below: `canDeleteSale` is « confirmed sale AND net
      // invoiced == 0 » (see ProjectPermissionsResponse), so removing the last invoice can unlock
      // « Supprimer la vente ». Refreshing the invoice list alone leaves the kebab on the guards of
      // the previous state until the user presses F5.
      await deleteProjectInvoice(id, target.id)
      await refetchProjectDetails()
      await refetchInvoices()
      showSuccessToast(`Facture ${target.number} supprimée — son numéro est de nouveau disponible`)
      setDeleteInvoiceTarget(null)
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      if (code === 'NOT_LAST_INVOICE') {
        showErrorToast('Une facture plus récente a été émise — suppression impossible')
        await refetchInvoices()
        setDeleteInvoiceTarget(null)
      } else if (code === 'CONCURRENCY_CONFLICT') {
        showErrorToast('La facturation a changé entre-temps, réessayez')
        await refetchInvoices()
        setDeleteInvoiceTarget(null)
      } else {
        showErrorToast('La suppression a échoué, réessayez')
      }
    } finally {
      setIsDeletingInvoice(false)
    }
  }

  // SOLY-ORDER-002 fiche 20 — « retirer un devis de la vente ». Only offered on a quote that is
  // actually selling: an accepted version on a live sale. Removing the LAST one deletes the sale,
  // which is why the confirmation copy branches on the response.
  const [removeQuoteId, setRemoveQuoteId] = useState<string | null>(null)
  const [isRemovingQuote, setIsRemovingQuote] = useState(false)

  const handleConfirmRemoveQuote = async (reason: string) => {
    if (!id || !removeQuoteId) return
    try {
      setIsRemovingQuote(true)
      const res = await removeQuoteFromSaleV2(id, removeQuoteId, reason)
      setRemoveQuoteId(null)
      showSuccessToast(
        res.saleEmptied
          ? 'Devis retiré — la vente ne contenait que celui-ci, elle a été supprimée'
          : 'Devis retiré de la vente',
      )
      await refetchProjectDetails()
      await refetchQuotes()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      if (code === 'CANNOT_REMOVE_QUOTE_INVOICED') {
        showErrorToast('Cette vente est facturée — créditez ou supprimez la facture avant de retirer un devis')
      } else if (code === 'CANNOT_REMOVE_QUOTE_OVER_CREDITED') {
        showErrorToast('Cette vente est sur-créditée — corrigez les notes de crédit avant de retirer un devis')
      } else if (code === 'QUOTE_NOT_IN_SALE') {
        showErrorToast("Ce devis ne fait plus partie de la vente")
        await refetchProjectDetails()
        setRemoveQuoteId(null)
      } else {
        showErrorToast('Le retrait a échoué, réessayez')
      }
    } finally {
      setIsRemovingQuote(false)
    }
  }

  // SOLY-ORDER-002 — delete sale / remove a quote from the sale / delete project
  const [isCancelSaleOpen, setIsCancelSaleOpen] = useState(false)
  const [isCancellingSale, setIsCancellingSale] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const [isArchiving, setIsArchiving] = useState(false)

  const handleConfirmCancelSale = async (reason: string) => {
    if (!id) return
    try {
      setIsCancellingSale(true)
      const result = await cancelSaleV2(id, reason)
      setIsCancelSaleOpen(false)
      showSuccessToast('Vente annulée')
      // Non-blocking: name the supplier orders that already left, so the user can follow up.
      for (const w of result.supplierOrderWarnings ?? []) {
        const ref = w.orderReference ? `, n° ${w.orderReference}` : ''
        showWarningToast(`Commande fournisseur déjà envoyée (${w.supplierName}${ref}) — à annuler chez le fournisseur`)
      }
      await refetchProjectDetails()
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      if (code === 'CANNOT_CANCEL_SALE_INVOICED') {
        showErrorToast('Ce projet est facturé — annulation impossible')
        await refetchProjectDetails()
        setIsCancelSaleOpen(false)
      } else if (code === 'SALE_ALREADY_CANCELLED') {
        // Stale UI (another tab/user already cancelled) — retrying is pointless, resync instead.
        showErrorToast('Cette vente est déjà annulée')
        await refetchProjectDetails()
        setIsCancelSaleOpen(false)
      } else if (code === 'SALE_NOT_CONFIRMED') {
        showErrorToast("Cette vente n'a pas encore été conclue — rien à annuler")
        await refetchProjectDetails()
        setIsCancelSaleOpen(false)
      } else {
        showErrorToast("L'annulation a échoué, réessayez")
      }
    } finally {
      setIsCancellingSale(false)
    }
  }


  const handleConfirmArchive = async () => {
    if (!id) return
    try {
      setIsArchiving(true)
      await archiveProjectV2(id)
      showSuccessToast('Projet supprimé')
      navigate('/')
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined
      if (code === 'CANNOT_DELETE_PROJECT_INVOICED') {
        showErrorToast('Ce projet est facturé — suppression impossible')
        await refetchProjectDetails()
        setIsArchiveOpen(false)
      } else {
        showErrorToast("La suppression a échoué, réessayez")
      }
    } finally {
      setIsArchiving(false)
    }
  }

  const { data: salesVatsData } = useProjectSalesVatsV2(id)

  const permissions = projectDetails?.permissions
  const allowUpdateProject = permissions?.allowUpdateProject === true
  const allowViewQuotes = permissions?.allowViewQuotes === true
  const allowCreateQuotes = permissions?.allowCreateQuotes === true
  const allowAcceptQuotes = permissions?.allowAcceptQuotes === true
  const allowViewInvoices = permissions?.allowViewInvoices === true
  const allowCreateInvoices = permissions?.allowCreateInvoices === true

  const canEdit = allowUpdateProject
  const canDeleteSale = permissions?.canDeleteSale === true
  const canDeleteProject = permissions?.canDeleteProject === true
  const isSaleCancelled = projectDetails?.saleStatus === 'cancelled'
  const hasDefaultVat = projectDetails?.defaultVat != null
  const canCreateQuotes = allowCreateQuotes && hasDefaultVat

  const [executionDetails, setExecutionDetails] = useState<ProjectExecutionDetailsDto | null>(null)
  const [isExecutionLoading, setIsExecutionLoading] = useState(false)
  const [executionError, setExecutionError] = useState<string | null>(null)

  const [isExecutionDialogOpen, setIsExecutionDialogOpen] = useState(false)
  const [isSavingExecution, setIsSavingExecution] = useState(false)

  const [desiredInstallationDateDraft, setDesiredInstallationDateDraft] = useState<string>('')
  const [floorKindDraft, setFloorKindDraft] = useState<FloorKind | null>(null)
  const [floorNumberDraft, setFloorNumberDraft] = useState<string>('')
  const [hasElevatorDraft, setHasElevatorDraft] = useState<boolean | null>(null)
  const [needsLiftDraft, setNeedsLiftDraft] = useState<boolean | null>(null)
  const [needsMeasurementsDraft, setNeedsMeasurementsDraft] = useState<boolean | null>(null)
  const [installersNoteDraft, setInstallersNoteDraft] = useState<string>('')

  const reloadExecution = async () => {
    if (!id) return
    try {
      setIsExecutionLoading(true)
      setExecutionError(null)
      const data = await getProjectExecutionDetails(id)
      setExecutionDetails(data ?? null)
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'Erreur lors du chargement des détails chantier'
      setExecutionError(message)
    } finally {
      setIsExecutionLoading(false)
    }
  }

  const handleEditConfiguration = () => {
    if (!id) return
    navigate(`/projects/${id}/configuration`, { state: { from: `/projects/${id}` } })
  }

  const handleCreateJobSite = () => {
    if (!id) return
    const customerId = projectDetails?.client?.id
    const hasBillingAddress = projectDetails?.client?.hasInvoicingAddress === true
    navigate(`/projects/${id}/job-site/new`, {
      state: {
        from: window.location.pathname,
        customerId,
        hasBillingAddress,
      },
    })
  }

  const handleEditCustomer = () => {
    const customerId = projectDetails?.client?.id
    if (!customerId) return
    navigate(`/clients/${customerId}/edit`, { state: { from: window.location.pathname } })
  }

  const handleStartRename = () => {
    if (!canEdit) return
    setNameDraft(projectDetails?.name?.trim() || '')
    setIsEditingName(true)
  }

  const handleCancelRename = () => {
    setNameDraft(projectDetails?.name?.trim() || '')
    setIsEditingName(false)
  }

  const handleSaveRename = async () => {
    const nextName = nameDraft.trim()

    if (!id) {
      showErrorToast('Erreur: ID de projet manquant')
      return
    }
    if (!nextName) {
      showErrorToast('Le nom du projet est requis')
      return
    }

    setIsRenaming(true)
    try {
      await updateProjectNameV2(id, nextName)
      showSuccessToast('Nom du projet mis à jour')
      setIsEditingName(false)
      await refetchProjectDetails()
    } catch {
      showErrorToast('Erreur de sauvegarde du nom du projet')
    } finally {
      setIsRenaming(false)
    }
  }

  const handleRequestCreateComplementaryQuote = () => {
    if (!allowCreateQuotes) return
    setComplementaryQuoteName('')
    setIsCreateQuoteConfirmOpen(true)
  }

  const handleRequestDuplicateVersion = (quoteId: string, version: QuoteVersionV2) => {
    if (!allowCreateQuotes) return
    setDuplicateTarget({
      quoteId,
      versionId: version.id,
      versionNumber: version.versionNumber,
      documentNumber: version.documentNumber,
      versionName: version.name,
    })
    setDuplicateDraftName('')
    setIsDuplicateDialogOpen(true)
  }

  const handleConfirmDuplicateVersion = async () => {
    if (!id) return
    if (!duplicateTarget) return
    const trimmedName = duplicateDraftName.trim()
    if (!trimmedName) {
      showInfoToast('Ajoutez un nom pour le nouveau brouillon')
      return
    }

    try {
      setIsDuplicatingVersion(true)
      await duplicateQuoteVersionV2(id, duplicateTarget.quoteId, duplicateTarget.versionId, { name: trimmedName })

      const response = await getProjectQuotesV2(id)
      await refetchQuotes()

      const foundQuote = (response.quotes || []).find((q) => q.id === duplicateTarget.quoteId) || null
      const foundVariant = (foundQuote?.variants || [])[0] || null
      const drafts = foundVariant?.draftVersions || []
      // TODO find a stable way. Eg. id of the draft returned by the API
      const foundDraft =
        drafts.find((d) => d.derivedFromVersionId === duplicateTarget.versionId) ||
        drafts.slice().sort((a, b) => (b.versionNumber ?? 0) - (a.versionNumber ?? 0))[0] ||
        null

      if (!foundDraft) {
        showErrorToast('Nouveau brouillon créé mais introuvable')
        return
      }

      navigate(`/projets/${id}/devis-v2/${duplicateTarget.quoteId}/versions/${foundDraft.id}/edit`)
    } catch {
      showErrorToast('Erreur lors de la création du nouveau brouillon')
    } finally {
      setIsDuplicatingVersion(false)
      setIsDuplicateDialogOpen(false)
      setDuplicateTarget(null)
      setDuplicateDraftName('')
    }
  }

  const handleRequestDeclineVersion = (quoteId: string, version: QuoteVersionV2) => {
    if (!allowAcceptQuotes) return
    if (version.acceptedAt || version.status === 'accepted') return
    if (version.isSuperseded || version.status === 'superseded') return
    if (version.status === 'declined') return

    setDeclineTarget({ quoteId, versionId: version.id, versionNumber: version.versionNumber, versionName: version.name })
    setIsDeclineDialogOpen(true)
  }

  const handleConfirmDeclineVersion = async () => {
    if (!id) return
    if (!declineTarget) return

    try {
      setIsDecliningVersion(true)
      await declineQuoteVersionV2(id, declineTarget.quoteId, declineTarget.versionId)
      await refetchQuotes()
      await refetchInvoices()
      await reloadExecution()
      showSuccessToast('Devis refusé')
    } catch {
      showErrorToast('Erreur lors du refus du devis')
    } finally {
      setIsDecliningVersion(false)
      setIsDeclineDialogOpen(false)
      setDeclineTarget(null)
    }
  }

  const handleRequestDeleteDraftVersion = (quoteId: string, version: QuoteVersionV2) => {
    if (!allowCreateQuotes) return
    setDeleteDraftTarget({ quoteId, versionId: version.id, versionNumber: version.versionNumber, versionName: version.name })
    setIsDeleteDraftDialogOpen(true)
  }

  const handleConfirmDeleteDraftVersion = async () => {
    if (!id) return
    if (!deleteDraftTarget) return

    try {
      setIsDeletingDraft(true)
      await deleteQuoteVersionV2(id, deleteDraftTarget.quoteId, deleteDraftTarget.versionId)
      await refetchQuotes()
      showSuccessToast('Brouillon supprimé')
    } catch {
      showErrorToast('Erreur lors de la suppression du brouillon')
    } finally {
      setIsDeletingDraft(false)
      setIsDeleteDraftDialogOpen(false)
      setDeleteDraftTarget(null)
    }
  }

  const handleCreateFirstQuote = async () => {
    if (!allowCreateQuotes) return
    await handleCreateQuote()
  }

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!id) return
      try {
        setIsExecutionLoading(true)
        setExecutionError(null)
        const data = await getProjectExecutionDetails(id)
        if (!mounted) return
        setExecutionDetails(data ?? null)
      } catch (e) {
        if (!mounted) return
        const message = e instanceof ApiError ? e.message : 'Erreur lors du chargement des détails chantier'
        setExecutionError(message)
      } finally {
        if (mounted) setIsExecutionLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [id])

  useEffect(() => {
    if (!isExecutionDialogOpen) return

    const toDateInputValue = (value: string | null | undefined) => {
      if (!value) return ''
      const datePart = value.includes('T') ? value.split('T')[0] : value
      return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : ''
    }

    setDesiredInstallationDateDraft(toDateInputValue(executionDetails?.desiredInstallationDate))
    setFloorKindDraft(executionDetails?.floorKind ?? null)
    setFloorNumberDraft(
      typeof executionDetails?.floorNumber === 'number' && Number.isFinite(executionDetails.floorNumber)
        ? String(executionDetails.floorNumber)
        : ''
    )
    setHasElevatorDraft(executionDetails?.hasElevator ?? null)
    setNeedsLiftDraft(executionDetails?.needsLift ?? null)
    setNeedsMeasurementsDraft(executionDetails?.needsMeasurements ?? null)
    setInstallersNoteDraft(executionDetails?.installersNote || '')
  }, [executionDetails, isExecutionDialogOpen])

  const handleCreateNewProjectInstead = () => {
    const customerId = projectDetails?.client?.id
    if (!customerId) return
    navigate(`/clients/${customerId}`)
    setIsCreateQuoteConfirmOpen(false)
  }

  const project = useMemo(() => {
    const reference = projectDetails?.reference?.trim() || '—'
    const jobSiteAddress = projectDetails?.jobSite?.address?.trim() || ''
    const jobSiteReference = projectDetails?.jobSite?.reference?.trim() || ''
    const jobSite = jobSiteAddress || '—'
    const typeLabel = projectDetails?.typeLabel?.trim() || projectDetails?.type?.trim() || ''
    const constructionLabel =
      projectDetails?.constructionTypeLabel?.trim() || projectDetails?.constructionType?.trim() || ''
    const supplierName = projectDetails?.supplierName?.trim() || ''
    const modelReference = projectDetails?.modelReference?.trim() || ''
    const vatPercent = projectDetails?.defaultVat != null ? Math.round(projectDetails.defaultVat * 100) : null

    const salesOwnerFullName = (projectDetails?.salesOwner?.fullName ?? '').trim() || '—'
    const salesOwnerInitials = (projectDetails?.salesOwner?.initials ?? '').trim() || '—'

    const quotes: QuoteGroup[] = (quotesResponse?.quotes || []).map((quote: QuoteV2) => {
      const primaryVariant = (quote.variants || [])[0] || null
      const generatedVersions = primaryVariant?.generatedVersions || []
      const draftVersions = primaryVariant?.draftVersions || []

      const acceptedTotalWithTax =
        generatedVersions.find((v) => (v.acceptedAt || v.status === 'accepted') && !v.isSuperseded)?.totalWithTax ?? null

      return {
        id: quote.id,
        reference: quote.documentNumber?.trim() || 'Devis',
        title: quote.name?.trim() || quote.statusLabel?.trim() || '',
        versionsCount: generatedVersions.length,
        acceptedTotalWithTax,
        draftVersions,
        generatedVersions,
      }
    })

    return {
      reference,
      name: projectDetails?.name?.trim() || 'Projet sans nom',
      customerId: projectDetails?.client?.id || null,
      customerName: projectDetails?.client?.name?.trim() || '—',
      salesOwner: { initials: salesOwnerInitials || '—', fullName: salesOwnerFullName },
      jobSite,
      jobSiteAddress,
      jobSiteReference,
      tags: [
        typeLabel,
        constructionLabel,
        vatPercent != null ? `TVA ${vatPercent}%` : '',
        modelReference ? `Modèle ${modelReference}` : '',
        supplierName ? `Fournisseur ${supplierName}` : '',
      ].filter((t) => t !== ''),
      quotes,
    }
  }, [projectDetails, quotesResponse])

  const acceptedQuotesTotal = project.quotes
    .map((q) => q.acceptedTotalWithTax)
    .filter((v): v is number => v != null)
    .reduce((sum, v) => sum + v, 0)

  const totalQuotesCount = project.quotes.length

  // SOLY-ORDER-002 — quotes actually standing behind the sale (accepted, not superseded). Drives
  // the cancel-sale copy: those quotes survive the deletion (unaccepted), whereas a sale without
  // them (a direct sale) leaves nothing behind. Also the « last quote » branch of the per-quote
  // removal dialog, which used to recompute the very same filter inline.
  const liveAcceptedQuotes = project.quotes.filter((q) =>
    q.generatedVersions.some((v) => isAcceptedQuoteVersion(v) && !isSupersededQuoteVersion(v)),
  )

  // SOLY-PLAN-001 MS-34 — sold signal from already-loaded state (no extra API call):
  // accepted non-superseded quote version, or confirmed direct sale (the invoicing header
  // total mirrors the confirmed order — same source as the `orderConfirmed` invoice gating).
  const hasAcceptedOrder = projectDetails?.isDirectSale
    ? (invoicesData?.project.totalWithTax ?? 0) > 0
    : project.quotes.some((q) => q.acceptedTotalWithTax != null)

  const { can, reasonIfDenied } = usePermissions()
  const planningSectionState = selectPlanningSectionState({
    planningEnabled: user?.features?.planning === true,
    executionDetails,
    executionDetailsSettled: !isExecutionLoading && executionError === null,
    hasAcceptedOrder,
  })

  const floorKindLabel = (k: FloorKind | null) => {
    if (k === 0) return 'Rez-de-chaussée'
    if (k === 1) return 'Étage numéroté'
    return '—'
  }

  const formatDateOnly = (value: string | null | undefined) => {
    if (!value) return '—'

    const datePart = value.includes('T') ? value.split('T')[0] : value
    const match = /^\d{4}-\d{2}-\d{2}$/.test(datePart)
    if (!match) return datePart

    const [y, m, d] = datePart.split('-')
    return `${d}/${m}/${y}`
  }

  const triStateLabel = (v: boolean | null) => {
    if (v === true) return 'Oui'
    if (v === false) return 'Non'
    return 'À préciser'
  }

  const triStateValue = (v: boolean | null): 'unset' | 'yes' | 'no' => {
    if (v === true) return 'yes'
    if (v === false) return 'no'
    return 'unset'
  }

  const parseTriState = (v: string): boolean | null => {
    if (v === 'yes') return true
    if (v === 'no') return false
    return null
  }

  // (2026-07-07) Temporarily disabled: the "Générer bordereau" button in the Exécution
  // section generated the work order from the accepted SalesOrder (GET /work-order/pdf).
  // It 404s ("PDF non disponible") whenever the quote isn't accepted yet, and users have
  // settled on generating the bordereau from the quotes instead (WorkOrderSelectionDialog,
  // POST /work-order/pdf-from-quotes). Button hidden below; handler kept for easy restore.
  // const handleGenerateWorkOrderPdf = () => {
  //   if (!id) return
  //   const url = `${config.apiBaseUrl}/projects/${id}/work-order/pdf`
  //   try {
  //     const a = document.createElement('a')
  //     a.href = url
  //     a.target = '_blank'
  //     a.rel = 'noopener noreferrer'
  //     a.click()
  //   } catch {
  //     showErrorToast("Impossible d'ouvrir le PDF")
  //   }
  // }

  const handleSaveExecutionDetails = async () => {
    if (!id) return
    const floorNumberParsed = floorNumberDraft.trim() === '' ? null : Number(floorNumberDraft)
    const floorNumber = Number.isFinite(floorNumberParsed) ? Math.trunc(floorNumberParsed as number) : null
    const isNumberedFloor = floorKindDraft === 1

    try {
      setIsSavingExecution(true)
      const updated = await upsertProjectExecutionDetails(id, {
        desiredInstallationDate: desiredInstallationDateDraft.trim() === '' ? null : desiredInstallationDateDraft,
        floorKind: floorKindDraft == null ? null : floorKindDraft,
        floorNumber: isNumberedFloor ? floorNumber : null,
        hasElevator: hasElevatorDraft,
        needsLift: needsLiftDraft,
        needsMeasurements: needsMeasurementsDraft,
        installersNote: installersNoteDraft.trim() === '' ? null : installersNoteDraft,
      })

      setExecutionDetails(updated)
      setIsExecutionDialogOpen(false)
      showSuccessToast('Détails chantier enregistrés')
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "Erreur lors de l'enregistrement"
      showErrorToast(message)
    } finally {
      setIsSavingExecution(false)
    }
  }

  const quotesSectionRef = useRef<HTMLElement | null>(null)
  const invoicesSectionRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab !== 'quotes' && tab !== 'invoices') return

    const target = tab === 'quotes' ? quotesSectionRef.current : invoicesSectionRef.current
    if (!target) return

    const t = window.setTimeout(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)

    return () => {
      window.clearTimeout(t)
    }
  }, [searchParams])

  const completionWarnings = useMemo(() => {
    if (!id || !projectDetails) return [] as Array<{ key: string; title: string; description: string; actionLabel: string; onClick: () => void }>

    const items: Array<{ key: string; title: string; description: string; actionLabel: string; onClick: () => void }> = []

    const missingVat = projectDetails.defaultVat == null
    if (missingVat && canEdit) {
      items.push({
        key: 'configure-project',
        title: 'Configurer le projet',
        description: 'TVA par défaut non configurée.',
        actionLabel: 'Configurer',
        onClick: handleEditConfiguration,
      })
    }

    const missingJobSite = !projectDetails.jobSite?.id
    if (missingJobSite && canEdit) {
      items.push({
        key: 'jobsite',
        title: 'Ajouter un chantier',
        description: 'Aucun chantier associé au projet.',
        actionLabel: 'Créer un chantier',
        onClick: handleCreateJobSite,
      })
    }

    // QA staging 2026-08-20 — an intervention is DESIGNED to open on « nom + téléphone » (the call
    // lasts a minute; the rest is completed later, before the invoice, which leaves days). Raising
    // the email and the billing address the second the file is created turns that deliberate
    // simplification into a reproach — ~600 amber banners a year for data we chose not to ask.
    // They come back as soon as the file carries an amount, i.e. when they actually block an invoice.
    // A chantier is untouched: its address is needed for the quote PDF, long before any invoice.
    // `invoicesData != null` is not defensive noise: useProjectInvoices sets data to NULL on a
    // failed fetch, so without it a network error would read as « still free » and silently drop
    // both reproaches for good — on a file that may well owe an invoice. It also removes the
    // pop-in, since the banner is not gated on the invoices being loaded.
    const isIntervention = projectDetails.nature === 'Intervention'
    const interventionStillFree = isIntervention
      && invoicesData != null
      && invoicesData.project.totalWithTax === 0

    const missingCustomerEmail = projectDetails.client?.hasContactEmail === false && !interventionStillFree
    if (missingCustomerEmail && canEdit) {
      items.push({
        key: 'customer-email',
        title: 'Ajouter un email client',
        description: 'Le client n’a pas d’email de contact.',
        actionLabel: 'Modifier le client',
        onClick: handleEditCustomer,
      })
    }

    const missingCustomerAddress = projectDetails.client?.hasInvoicingAddress === false && !interventionStillFree
    if (missingCustomerAddress && canEdit) {
      items.push({
        key: 'customer-address',
        title: 'Ajouter une adresse de facturation',
        description: 'Le client n’a pas d’adresse de facturation.',
        actionLabel: 'Modifier le client',
        onClick: handleEditCustomer,
      })
    }

    return items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, projectDetails, canEdit, invoicesData])

  const sortedInvoices = useMemo<ProjectInvoice[]>(() => {
    const invoices = invoicesData?.invoices || []
    return [...invoices].sort((a, b) => b.invoiceDate.localeCompare(a.invoiceDate))
  }, [invoicesData])

  // QA staging 2026-08-20 — a zero balance has TWO very different causes, and saying « Rien à
  // facturer » for both is the app asserting something false. On a file whose total is still zero,
  // the balance is zero because NO PRICE HAS BEEN SET, not because everything has been invoiced —
  // which is exactly the state of an intervention that has just been carried out (~310 files a
  // year: the price of a retouche is discovered after the passage). « Rien à facturer » there does
  // not merely fail to remind her; it tells her the opposite of the truth, and authorises the
  // oversight. The honest sentence is that the amount is not defined yet.
  // The actionable version — « travail effectué le 20/08, montant à définir » — needs the pose
  // status, and is what the 4th rule of « À facturer » delivers in lot 2. This removes the lie now.
  //
  // ⚠️ `isSaleCancelled` guards the OTHER way a total reaches zero. `OrderCancelled()` empties the
  // reference tax total (Soly.Invoicing.Domain/ProjectInvoicing.cs), so a chantier sold 12 000 €
  // and then cancelled also lands on totalWithTax == 0 — and there the amount WAS defined; it is
  // the sale that was called off. « Rien à facturer » is the sentence SOLY-ORDER-002 fiche 16 wrote
  // for exactly that file, and it must keep it.
  const nothingPricedYet = !isSaleCancelled && (invoicesData?.project.totalWithTax ?? 0) === 0
  // SOLY-ORDER-002 fiche 16 — see hasNothingLeftToInvoice: derived from the balance, not a status.
  const remainingBalance = invoicesData?.project.totalRemaining
  const nothingLeftToInvoice = hasNothingLeftToInvoice(remainingBalance)

  // SOLY-REGIE-002 — de muet à actionnable. Les deux valeurs lues ici sont des FAITS servis par le
  // backend : le front ne connaît ni les ancres de pose ni les statuts de work item, et s'il se
  // mettait à les recalculer, la fiche et « À facturer » finiraient par se contredire sur le même
  // dossier. `interventionPricing` est null sur un chantier — c'est ce qui garantit qu'aucun des
  // deux éléments ci-dessous ne peut y apparaître (AC-4).
  const interventionPricing = invoicesData?.interventionPricing ?? null
  const freeTouchUpDecidedAt = interventionPricing?.notBilledAt ?? null
  // ⚠️ On AJOUTE des états, on ne réécrit pas `nothingPricedYet` : « Rien à facturer » sur une
  // vente annulée reste mot pour mot ce que SOLY-ORDER-002 fiche 16 a écrit, et c'est la
  // régression la plus facile à introduire ici. La résolution est une fonction pure et testée —
  // c'est l'ordre des quatre branches qui porte la règle, pas la mise en page ci-dessous.
  const invoicingSummaryState = resolveInvoicingSummaryState({ interventionPricing, nothingPricedYet })

  const [isFreeTouchUpOpen, setIsFreeTouchUpOpen] = useState(false)
  const { isPending: isDecisionPending, error: decisionError, markNotBilled, clearNotBilled } =
    useNotBilledDecision(async () => {
      setIsFreeTouchUpOpen(false)
      await refetchInvoices()
    })

  const normalizeVatPercent = (value: number) => {
    if (!Number.isFinite(value)) return null
    const asPercent = value <= 1 ? value * 100 : value
    return Math.round(asPercent)
  }

  const availableVatPercents = useMemo(() => {
    const vats = salesVatsData?.availableVats || []
    return vats
      .map((v) => v.value)
      .map((v) => normalizeVatPercent(v))
      .filter((v): v is number => v != null)
      .filter((v, idx, arr) => arr.indexOf(v) === idx)
      .sort((a, b) => a - b)
  }, [salesVatsData])

  const handleOpenInvoicePdf = async (invoiceId: string) => {
    if (!id) return

    try {
      const pdf = await getProjectInvoicePdfUrl(id, invoiceId)
      if (pdf.url) {
        window.open(pdf.url, '_blank', 'noopener,noreferrer')
      }
    } catch {
      showErrorToast("Impossible d'ouvrir le PDF")
    }
  }

  const handleOpenQuoteVersionPdf = async (quoteId: string, versionId: string) => {
    if (!id) return
    try {
      const pdf = await getProjectQuoteVersionPdfUrlV2(id, quoteId, versionId)
      if (pdf.url) {
        window.open(pdf.url, '_blank', 'noopener,noreferrer')
        return
      }
      showErrorToast("Impossible d'ouvrir le PDF")
    } catch {
      showErrorToast("Impossible d'ouvrir le PDF")
    }
  }

  const handleCreateQuote = async (name?: string) => {
    if (!id) return
    if (!canEdit) return

    // Check if default VAT is defined
    if (projectDetails?.defaultVat == null) {
      showErrorToast('Veuillez définir une TVA par défaut avant de créer un devis')
      return
    }

    const trimmedName = name?.trim()

    setIsCreatingComplementaryQuote(true)
    try {
      const created = await createProjectQuoteDraftV2(id, { name: trimmedName })
      await refetchQuotes()

      const primaryVariant = (created.variants || [])[0] || null
      const draft = (primaryVariant?.draftVersions || [])[0] || null

      if (!draft) {
        showErrorToast('Devis créé mais aucun brouillon actif')
        return
      }

      navigate(`/projets/${id}/devis-v2/${created.id}/versions/${draft.id}/edit`)
    } catch {
      showErrorToast('Erreur lors de la création du devis complémentaire')
    } finally {
      setIsCreatingComplementaryQuote(false)
    }
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      <TopBar />
      <main className="flex-1 overflow-y-auto bg-muted/40">
        <div className="sticky top-0 z-40 border-b border-border bg-background">
          <div className="mx-auto max-w-7xl px-4 py-3 sm:px-8 lg:px-12">
            {/* SOLY-NAV-001 — the navigation band: where am I, and where can I go from here. The
                trail gives way when the row is narrow; the door out never does. */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
                <Link to="/home" className="inline-flex items-center gap-1 hover:text-foreground">
                  <ArrowLeft className="h-4 w-4" />
                  <span>Accueil</span>
                </Link>
                <span>/</span>
                {projectLoading ? (
                  <Skeleton className="h-4 w-40" />
                ) : project.customerId ? (
                  <Link to={`/clients/${project.customerId}`} className="min-w-0 truncate hover:text-foreground">
                    {project.customerName}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate">{project.customerName}</span>
                )}
                <span>/</span>
                {projectLoading ? (
                  <Skeleton className="h-4 w-24" />
                ) : (
                  <span className="shrink-0 text-foreground font-medium">{project.reference}</span>
                )}
              </div>

              {id && (
                <OpenInPlanningLink
                  /* Remount on project change: the link's own state is trivial, but keying it here
                     documents that this block is per-project and keeps it honest if it grows. */
                  key={id}
                  projectId={id}
                  /* 'picker' is the state that means the cockpit knows this project. On anything
                     else the page already offers the right gesture (the activation card), and this
                     door would open on the drawer's PROJECT_NOT_FOUND. */
                  isPlanningInitialized={planningSectionState === 'picker'}
                />
              )}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {projectLoading ? (
                    <Skeleton className="h-6 w-96" />
                  ) : !isEditingName ? (
                    <>
                      <h1 className="text-xl font-semibold truncate">{project.name}</h1>
                      {projectDetails?.isDirectSale && (
                        <Badge className="rounded-md border-0 bg-indigo-100 text-indigo-700 shrink-0">
                          Vente directe
                        </Badge>
                      )}
                      {isSaleCancelled && (
                        <Badge
                          className="rounded-md border-0 bg-slate-100 text-slate-700 shrink-0"
                          data-testid="project-sale-cancelled-badge"
                        >
                          Vente annulée
                        </Badge>
                      )}
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={handleStartRename}
                          aria-label="Renommer"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  ) : (
                    <div className="flex w-full flex-wrap items-center gap-2">
                      <Input
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                        disabled={isRenaming}
                        className="h-9 max-w-[520px]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleSaveRename()
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault()
                            handleCancelRename()
                          }
                        }}
                        autoFocus
                      />
                      <Button
                        variant="default"
                        size="icon"
                        className="h-9 w-9"
                        onClick={handleSaveRename}
                        disabled={isRenaming || !nameDraft.trim()}
                        aria-label="Enregistrer"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-9 w-9"
                        onClick={handleCancelRename}
                        disabled={isRenaming}
                        aria-label="Annuler"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {projectLoading
                    ? Array.from({ length: 4 }).map((_, idx) => (
                      <Skeleton key={idx} className="h-6 w-24" />
                    ))
                    : project.tags.map((t) => (
                      <Badge key={t} variant="outline" className="rounded-sm">
                        {t}
                      </Badge>
                    ))}
                </div>

                <div className="mt-2 text-sm text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  <span className="truncate">
                    {projectLoading ? 'Chargement…' : project.jobSiteReference ? `Chantier ${project.jobSiteReference} • ${project.jobSite}` : project.jobSite}
                  </span>
                </div>
              </div>

              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="text-xs font-semibold bg-primary/10 text-primary">
                      {project.salesOwner.initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Commercial</p>
                    <p className="text-sm font-medium truncate">{project.salesOwner.fullName}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  {id && projectDetails && (
                    <ProjectLanguageControl
                      projectId={id}
                      currentLanguageId={projectDetails.preferredLanguageId}
                      customerLanguageId={projectDetails.client?.preferredLanguageId}
                      canEdit={canEdit}
                      onUpdated={() => refetchProjectDetails()}
                    />
                  )}
                  {canEdit && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 gap-2"
                      onClick={handleEditConfiguration}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Modifier
                    </Button>
                  )}
                  <ProjectActionsMenu
                    canDeleteProject={canDeleteProject}
                    canDeleteSale={canDeleteSale}
                    onDeleteProject={() => setIsArchiveOpen(true)}
                    onDeleteSale={() => setIsCancelSaleOpen(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-8 lg:px-12 space-y-8">
          {completionWarnings.length > 0 ? (
            <Card className="py-3 border-amber-200 bg-amber-50/40">
              <CardContent className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold">À compléter</p>
                      <p className="text-xs text-muted-foreground">Certaines informations manquent pour avancer sereinement.</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  {completionWarnings.map((w) => (
                    <div key={w.key} className="rounded-lg border bg-background/60 p-2.5 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{w.title}</p>
                        <p className="text-xs text-muted-foreground">{w.description}</p>
                      </div>
                      <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={w.onClick}>
                        {w.actionLabel}
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {allowViewQuotes && !projectDetails?.isDirectSale ? (
            <section ref={quotesSectionRef} id="quotes" className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold">Devis</h2>
                  <p className="text-sm text-muted-foreground">
                    {totalQuotesCount} devis - Total accepté : {formatCurrency(acceptedQuotesTotal)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {allowCreateQuotes && hasDefaultVat && id && (
                    <WinnerImportButton
                      testId="project-winner-import-btn"
                      size="sm"
                      entryProject={{ id, name: projectDetails?.name?.trim() || null }}
                      onImported={() => void refetchQuotes()}
                    />
                  )}
                  {project.quotes.some((q) => q.generatedVersions.length > 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsWorkOrderDialogOpen(true)}
                    >
                      Générer bordereau
                    </Button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                {quotesError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
                    Erreur lors du chargement des devis
                  </div>
                ) : isQuotesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-20 w-full" />
                  </div>
                ) : project.quotes.length === 0 ? (
                  <div className="rounded-lg border bg-card p-6 text-sm text-muted-foreground">
                    <div className="space-y-4">
                      <div className="text-center">Aucun devis</div>
                      {/* A project with no default VAT cannot carry a quote -- but hiding the button
                          left a patron on his first project with an empty card and nothing to click.
                          Show it disabled, and name what is missing. */}
                      {allowCreateQuotes ? (
                        <div className="flex flex-col items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="quote-create-btn"
                            className="gap-2"
                            onClick={handleCreateFirstQuote}
                            disabled={!hasDefaultVat || isCreatingComplementaryQuote}
                            aria-describedby={!hasDefaultVat ? 'quote-create-blocked-reason' : undefined}
                            title={!hasDefaultVat ? "Choisissez d'abord le type de travaux (il fixe la TVA)" : undefined}
                          >
                            <Plus className="h-4 w-4" />
                            Créer un devis
                          </Button>
                          {!hasDefaultVat ? (
                            <p id="quote-create-blocked-reason" className="text-center text-xs" data-testid="quote-create-blocked-reason">
                              Choisissez d'abord le type de travaux (il fixe la TVA).{' '}
                              <button
                                type="button"
                                data-testid="quote-create-blocked-configure-link"
                                className="underline underline-offset-2 hover:no-underline"
                                onClick={handleEditConfiguration}
                              >
                                Configurer le projet
                              </button>
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : (
                  project.quotes.map((q) => (
                    <Card key={q.id} className="py-3">
                      <CardHeader className="pb-2">
                        <div className="flex flex-col gap-1">
                          <CardTitle className="text-base flex flex-wrap items-center gap-2">
                            <span className="truncate">{q.reference}</span>
                            <span className="text-muted-foreground font-normal">{q.title}</span>
                            <span className="text-xs text-muted-foreground">{q.versionsCount} versions</span>
                            {q.acceptedTotalWithTax != null && (
                              <Badge className="rounded-md bg-green-100 text-green-700 border-0">
                                {formatCurrency(q.acceptedTotalWithTax)} TVAC
                              </Badge>
                            )}
                            <WinnerSourceFileButton quoteId={q.id} testId="quotes-winner-source-btn" wrapperClassName="ml-auto" />
                          </CardTitle>
                        </div>
                      </CardHeader>

                      <CardContent className="space-y-2">
                        <div className="space-y-4">
                          {q.draftVersions.length > 0 ? (
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-muted-foreground">Brouillons (édition)</div>
                              <div className="space-y-2">
                                {q.draftVersions.map((draft) => (
                                  <div
                                    key={draft.id}
                                    className="rounded-lg border border-slate-300 bg-muted/60 p-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                                  >
                                    <div className="min-w-0">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold">V{draft.versionNumber}</span>
                                        <span className="text-sm font-semibold">{draft.name?.trim() || 'Brouillon'}</span>
                                        {draft.derivedFromVersionNumber != null ? (
                                          <span className="text-xs text-muted-foreground">(depuis V{draft.derivedFromVersionNumber})</span>
                                        ) : null}
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap items-center justify-between gap-3 sm:justify-end">
                                      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                        <Calendar className="h-3.5 w-3.5" />
                                        <span>Créé {formatDate(draft.createdAt, 'd MMM yyyy')}</span>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          data-testid="quote-draft-duplicate-btn"
                                          className="h-8 px-2"
                                          onClick={() => handleRequestDuplicateVersion(q.id, draft)}
                                          disabled={!canCreateQuotes || isDuplicatingVersion}
                                          aria-label="Dupliquer"
                                        >
                                          <Copy className="h-4 w-4" />
                                          <span className="hidden sm:inline">Dupliquer</span>
                                        </Button>

                                        {q.draftVersions.length + q.generatedVersions.length > 1 ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            data-testid="quote-draft-delete-btn"
                                            className="h-8 px-2 text-destructive hover:text-destructive"
                                            onClick={() => handleRequestDeleteDraftVersion(q.id, draft)}
                                            disabled={!canCreateQuotes || isDeletingDraft}
                                            aria-label="Supprimer"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            <span className="hidden sm:inline">Supprimer</span>
                                          </Button>
                                        ) : null}

                                        <Button
                                          size="sm"
                                          data-testid="quote-draft-continue-btn"
                                          className="h-8"
                                          disabled={!canCreateQuotes}
                                          onClick={() => {
                                            if (!hasDefaultVat) {
                                              showErrorToast('Veuillez définir une TVA par défaut avant de continuer le devis')
                                              return
                                            }
                                            navigate(`/projets/${id}/devis-v2/${q.id}/versions/${draft.id}/edit`)
                                          }}
                                        >
                                          Continuer
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          {q.generatedVersions.length > 0 ? (
                            <div className="space-y-2">
                              <div className="text-xs font-medium text-muted-foreground">Versions générées</div>
                              <div className="space-y-2">
                                {q.generatedVersions.map((v) => {
                                  return (
                                    <div
                                      key={v.id}
                                      className={`rounded-lg border flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between ${
                                        v.isSuperseded || v.status === 'superseded'
                                          ? 'bg-slate-50 border-slate-200 opacity-60 py-0.5 px-2.5'
                                          : v.status === 'declined'
                                            ? 'bg-slate-50 border-slate-200 opacity-60 py-0.5 px-2.5'
                                            : 'bg-background py-1.5 px-3'
                                      }`}
                                    >
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-sm font-semibold">
                                          V{v.versionNumber}
                                        </span>
                                        <span data-testid="quote-status-badge">{getQuoteStatusBadge(v)}</span>
                                        {v.name?.trim() ? (
                                          <span className="text-sm font-semibold text-muted-foreground truncate">{v.name.trim()}</span>
                                        ) : null}
                                        {v.derivedFromVersionNumber != null ? (
                                          <span className="text-xs text-muted-foreground">(depuis V{v.derivedFromVersionNumber})</span>
                                        ) : null}
                                        
                                          <span className="text-xs text-muted-foreground ml-2">
                                            Créé {formatDate(v.createdAt, 'd MMM yyyy')}
                                            {v.acceptedAt ? ` • accepté ${formatDate(v.acceptedAt, 'd MMM yyyy')}` : ''}
                                          </span>
                                        
                                      </div>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                                      <div className="text-right min-w-[120px]">
                                        <div className="text-sm font-semibold">{formatCurrency(v.totalWithTax || 0)}</div>
                                        <div className="text-[11px] text-muted-foreground">{formatCurrency(v.totalBeforeTax || 0)} HTVA</div>
                                      </div>

                                      <div className="flex items-center gap-2">
                                        {SHOW_QUOTE_REMOVE_FROM_SALE && allowAcceptQuotes && isAcceptedQuoteVersion(v) && !isSupersededQuoteVersion(v) && !isSaleCancelled ? (
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid="quote-remove-from-sale-btn"
                                            className="h-8 gap-2 border-amber-200 text-amber-800 hover:bg-amber-50 hover:text-amber-800"
                                            onClick={() => setRemoveQuoteId(q.id)}
                                          >
                                            <X className="h-4 w-4" />
                                            Retirer de la vente
                                          </Button>
                                        ) : null}

                                        {allowAcceptQuotes && (canAcceptIssuedQuoteVersion(v) || canDeclineIssuedQuoteVersion(v)) ? (
                                          <div className="flex items-center gap-2 rounded-md border bg-background p-1">
                                            {canAcceptIssuedQuoteVersion(v) ? (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                data-testid="quote-version-accept-btn"
                                                className="h-8 gap-2 border-green-200 text-green-800 hover:bg-green-50 hover:text-green-800"
                                                onClick={() => {
                                                  setAcceptOrderQuoteId(q.id)
                                                  setAcceptOrderVersionId(v.id)
                                                  setAcceptOrderVersionNumber(v.versionNumber)
                                                  setAcceptOrderVersionName(v.name ?? null)
                                                  setIsAcceptOrderOpen(true)
                                                }}
                                              >
                                                <Check className="h-4 w-4" />
                                                Accepter
                                              </Button>
                                            ) : null}

                                            {canDeclineIssuedQuoteVersion(v) ? (
                                              <Button
                                                variant="outline"
                                                size="sm"
                                                data-testid="quote-version-decline-btn"
                                                className="h-8 gap-2 border-red-200 text-red-800 hover:bg-red-50 hover:text-red-800"
                                                onClick={() => handleRequestDeclineVersion(q.id, v)}
                                              >
                                                <X className="h-4 w-4" />
                                                Refuser
                                              </Button>
                                            ) : null}
                                          </div>
                                        ) : null}

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          data-testid="quote-version-duplicate-btn"
                                          className="h-8 px-2"
                                          onClick={() => handleRequestDuplicateVersion(q.id, v)}
                                          disabled={!canCreateQuotes || isDuplicatingVersion}
                                          aria-label="Dupliquer"
                                        >
                                          <Copy className="h-4 w-4" />
                                          <span className="hidden sm:inline">Dupliquer</span>
                                        </Button>

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          data-testid="quote-version-pdf-btn"
                                          className="h-8 px-2"
                                          onClick={() => handleOpenQuoteVersionPdf(q.id, v.id)}
                                          aria-label="Ouvrir le PDF"
                                          title="Ouvrir le PDF"
                                        >
                                          <FileText className="h-4 w-4" />
                                          <span className="hidden sm:inline">PDF</span>
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                  )
                                })}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>

            {canCreateQuotes && !isQuotesLoading && !quotesError && project.quotes.length > 0 && (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="quote-complementary-btn"
                  className="gap-2"
                  onClick={handleRequestCreateComplementaryQuote}
                  disabled={isCreatingComplementaryQuote}
                >
                  <Plus className="h-4 w-4" />
                  Nouveau devis (complémentaire)
                </Button>
              </div>
            )}
            </section>
          ) : null}

          {projectDetails?.isDirectSale && id && (
            <>
              <Separator />
              <DirectSaleOrderSection
                projectId={id}
              />
            </>
          )}

          <AlertDialog
            open={isCreateQuoteConfirmOpen}
            onOpenChange={(next) => {
              setIsCreateQuoteConfirmOpen(next)
              if (!next) {
                setComplementaryQuoteName('')
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <div className="flex items-start justify-between gap-4">
                  <AlertDialogTitle>C'est pour la même commande client ?</AlertDialogTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 -mr-2 -mt-2"
                    onClick={() => setIsCreateQuoteConfirmOpen(false)}
                    aria-label="Fermer"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </AlertDialogHeader>

              <AlertDialogDescription>
                Ajouter ce devis au projet <span className="font-medium text-foreground">{project.name}</span> ?
              </AlertDialogDescription>

              <div className="space-y-2">
                <Label>Nom du devis complémentaire</Label>
                <Input
                  value={complementaryQuoteName}
                  onChange={(e) => setComplementaryQuoteName(e.target.value)}
                  placeholder="Ex: Plan de travail, électros"
                  disabled={isCreatingComplementaryQuote}
                />
              </div>

              <AlertDialogFooter className="sm:items-center">
                {projectDetails?.client?.id ? (
                  <Button variant="outline" onClick={handleCreateNewProjectInstead}>
                    ← Créer un autre projet
                  </Button>
                ) : null}
                <AlertDialogAction
                  onClick={async () => {
                    setIsCreateQuoteConfirmOpen(false)
                    await handleCreateQuote(complementaryQuoteName)
                  }}
                  disabled={isCreatingComplementaryQuote || !complementaryQuoteName.trim()}
                >
                  Oui
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={isDeclineDialogOpen} onOpenChange={setIsDeclineDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Refuser le devis</AlertDialogTitle>
                <AlertDialogDescription>
                  Refuser la version <span className="font-medium">V{declineTarget?.versionNumber}</span>
                  {declineTarget?.versionName?.trim() ? (
                    <span className="font-medium"> — “{declineTarget.versionName.trim()}”</span>
                  ) : null}{' '}
                  ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDecliningVersion}>Annuler</AlertDialogCancel>
                <AlertDialogAction disabled={isDecliningVersion} onClick={handleConfirmDeclineVersion}>
                  {isDecliningVersion ? 'Refus…' : 'Refuser'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <DeleteInvoiceDialog
            invoice={deleteInvoiceTarget}
            isDeleting={isDeletingInvoice}
            onConfirm={handleConfirmDeleteInvoice}
            onCancel={() => setDeleteInvoiceTarget(null)}
          />

          {isCancelSaleOpen && id && (
            <CancelSaleDialog
              projectId={id}
              hasAcceptedQuotes={liveAcceptedQuotes.length > 0}
              isSubmitting={isCancellingSale}
              onConfirm={handleConfirmCancelSale}
              onCancel={() => setIsCancelSaleOpen(false)}
            />
          )}


          {removeQuoteId && (
            <RemoveQuoteFromSaleDialog
              isLastQuote={liveAcceptedQuotes.length <= 1}
              isSubmitting={isRemovingQuote}
              onConfirm={handleConfirmRemoveQuote}
              onCancel={() => setRemoveQuoteId(null)}
            />
          )}

          {id && (
            <DeleteProjectDialog
              open={isArchiveOpen}
              projectId={id}
              isSubmitting={isArchiving}
              onConfirm={handleConfirmArchive}
              onCancel={() => setIsArchiveOpen(false)}
            />
          )}

          <AlertDialog open={isDeleteDraftDialogOpen} onOpenChange={setIsDeleteDraftDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer le brouillon</AlertDialogTitle>
                <AlertDialogDescription>
                  Supprimer définitivement le brouillon <span className="font-medium">V{deleteDraftTarget?.versionNumber}</span>
                  {deleteDraftTarget?.versionName?.trim() ? (
                    <span className="font-medium"> — “{deleteDraftTarget.versionName.trim()}”</span>
                  ) : null}{' '}
                  ?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeletingDraft}>Annuler</AlertDialogCancel>
                <AlertDialogAction disabled={isDeletingDraft} onClick={handleConfirmDeleteDraftVersion}>
                  {isDeletingDraft ? 'Suppression…' : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog
            open={isDuplicateDialogOpen}
            onOpenChange={(next) => {
              setIsDuplicateDialogOpen(next)
              if (!next) {
                setDuplicateDraftName('')
              }
            }}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Dupliquer une version</AlertDialogTitle>
                <AlertDialogDescription>
                  Vous allez créer un <span className="font-medium">nouveau brouillon</span> à partir de la version{' '}
                  <span className="font-medium">V{duplicateTarget?.versionNumber}</span>
                  {duplicateTarget?.documentNumber ? <span className="font-medium"> ({duplicateTarget.documentNumber})</span> : null}
                  {duplicateTarget?.versionName?.trim() ? (
                    <>
                      {' '}
                      <span className="font-medium">“{duplicateTarget.versionName.trim()}”</span>
                    </>
                  ) : null}.
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="space-y-2">
                <Label>Donnez un Nom à la nouvelle version</Label>
                <Input value={duplicateDraftName} placeholder="Ex: option îlot, client - ajustement prix" onChange={(e) => setDuplicateDraftName(e.target.value)} />
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDuplicatingVersion}>Annuler</AlertDialogCancel>
                <AlertDialogAction disabled={isDuplicatingVersion || !duplicateDraftName.trim()} onClick={handleConfirmDuplicateVersion}>
                  {isDuplicatingVersion ? 'Duplication…' : 'Dupliquer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <WorkOrderSelectionDialog
            open={isWorkOrderDialogOpen}
            onOpenChange={setIsWorkOrderDialogOpen}
            projectId={id ?? ''}
            quotes={quotesResponse?.quotes ?? []}
          />

          <QuoteAcceptVersionDialog
            open={isAcceptOrderOpen}
            onOpenChange={(next) => {
              setIsAcceptOrderOpen(next)
              if (!next) {
                setAcceptOrderQuoteId(null)
                setAcceptOrderVersionId(null)
                setAcceptOrderVersionNumber(null)
                setAcceptOrderVersionName(null)
              }
            }}
            versionNumber={acceptOrderVersionNumber}
            versionName={acceptOrderVersionName}
            onAccept={async ({ customerAcceptedDate }) => {
              if (!id || !acceptOrderQuoteId || !acceptOrderVersionId) return
              try {
                // Accepting a quote is a sale lifecycle transition, so it moves the header guards
                // (`canDeleteSale`, `saleStatus`) that live on the project payload — not only the
                // quote and invoice lists. Found by TC-028 on 2026-09-09 (SOLY-E2E-001): without
                // the refetch below the kebab kept offering « Supprimer le projet » alone, and
                // « Supprimer la vente » only appeared after a manual refresh.
                await acceptQuoteVersionV2(id, acceptOrderQuoteId, acceptOrderVersionId, { customerAcceptedDate })
                await refetchProjectDetails()
                await refetchQuotes()
                await refetchInvoices()
                await reloadExecution()
                showSuccessToast('Devis accepté')
              } catch {
                showErrorToast("Erreur lors de la validation de la commande")
              }
            }}
          />

          {/* SOLY-IMPORT-003 S11 — how each sold line is supplied lives on the sale's own page, behind quote_import (S19). */}
          {id && hasAcceptedOrder && !isSaleCancelled && hasModule(MODULES.QUOTE_IMPORT) && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5" data-testid="project-sale-supply-card">
              <div className="min-w-0">
                <div className="text-sm font-medium">Appro des lignes vendues</div>
                <div className="text-xs text-muted-foreground">Pour chaque ligne : acheter (chez qui), fabriquer ou rien.</div>
              </div>
              <Button asChild size="sm" variant="outline">
                <Link to={`/projects/${id}/vente`} data-testid="project-sale-supply-link">Ouvrir la vente</Link>
              </Button>
            </div>
          )}

          {/* SOLY-PLAN-001 MS-34 — planning block: picker when initialized, activation empty
              state for projects sold before the feature, nothing otherwise (states in
              planningSectionState.ts). Never show the picker on a non-initialized project. */}
          {id && planningSectionState === 'picker' && can(PERMISSIONS.ORDERS_READ) && (
            <>
              <Separator />
              {/* key: a different project is a different question — remount rather than let the
                  previous project's answer sit on screen while the new read is in flight. */}
              <ClientOrderPicker key={id} projectId={id} />
            </>
          )}
          {/* Pas de garde `planning:manage` ici : la carte porte elle-même le refus (bouton grisé
              + raison au survol), et la masquer laisserait un commercial devant un chantier vendu
              avant la feature sans rien du tout — ni le geste, ni le texte qui explique ce qu'est
              le suivi de chantier. Deux mécanismes pour un même refus, dont l'un annulait l'autre.
              Règle appliquée dans tout le diff : un droit de *lecture* masque la section (cf. le
              picker au-dessus, gaté `orders:read`), un droit de *gestion* grise le geste. */}
          {id && planningSectionState === 'activation' && (
            <>
              <Separator />
              <PlanningActivationCard projectId={id} onActivated={reloadExecution} />
            </>
          )}

          {allowViewInvoices ? (
            <>
              <Separator />

              <section ref={invoicesSectionRef} id="invoices" className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Facturation</h2>
                <p className="text-sm text-muted-foreground">Suivi des factures</p>
              </div>

              {allowCreateInvoices && (
                <div className="flex items-center gap-2">
                  {(() => {
                    const remainingAmount = invoicesData?.project.totalRemaining ?? 0
                    if (projectDetails?.isDirectSale) {
                      const hasAnyInvoice = (invoicesData?.invoices.length ?? 0) > 0
                      const orderConfirmed = (invoicesData?.project.totalWithTax ?? 0) > 0
                      if (!hasAnyInvoice && orderConfirmed) return (
                        <Button variant="outline" size="sm" data-testid="invoice-direct-btn" className="gap-2" onClick={() => navigate(`/projets/${id}/invoices/direct`)}>
                          <Plus className="h-4 w-4" />
                          Facture directe
                        </Button>
                      )
                      if (remainingAmount > 0) return (
                        <Button variant="outline" size="sm" data-testid="invoice-supplementary-btn" className="gap-2" onClick={() => navigate(`/projets/${id}/invoices/standalone`)}>
                          <Plus className="h-4 w-4" />
                          Facture supplémentaire
                        </Button>
                      )
                      return null
                    }
                    // Regular project (non direct sale)
                    const hasFinalInvoice = hasActiveFinalInvoice(invoicesData?.invoices ?? [])
                    const showSupplementaryButton = hasFinalInvoice && remainingAmount > 0
                    return showSupplementaryButton ? (
                      <Button variant="outline" size="sm" data-testid="invoice-supplementary-btn" className="gap-2" onClick={() => navigate(`/projets/${id}/invoices/standalone`)}>
                        <Plus className="h-4 w-4" />
                        Facture supplémentaire
                      </Button>
                    ) : !hasFinalInvoice ? (
                      <Button variant="outline" size="sm" data-testid="invoice-create-btn" className="gap-2" onClick={() => navigate(`/projets/${id}/invoices/new`)}>
                        <Plus className="h-4 w-4" />
                        Nouvelle facture
                      </Button>
                    ) : null
                  })()}
                  {invoicesData && invoicesData.invoices.length > 0 && (() => {
                    const remaining = invoicesData.project.totalRemaining ?? 0
                    const isOverInvoiced = remaining < -0.01
                    return (
                      <Button
                        variant={isOverInvoiced ? 'destructive' : 'ghost'}
                        size="sm"
                        className={isOverInvoiced ? 'gap-2' : 'gap-2 text-muted-foreground hover:text-red-600'}
                        onClick={() => navigate(`/projets/${id}/invoices/credit-note`)}
                      >
                        <Minus className="h-4 w-4" />
                        {isOverInvoiced
                          ? `Note de crédit (${formatCurrency(Math.abs(remaining))} à créditer)`
                          : 'Note de crédit'}
                      </Button>
                    )
                  })()}
                </div>
              )}
            </div>

            {invoicesError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
                Erreur lors du chargement des factures
              </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
              <Card className="py-3">
                <CardContent>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total projet (TVAC)</p>
                  <p data-testid="invoice-total-project" className="mt-2 text-xl font-semibold">
                    {isInvoicesLoading
                      ? '—'
                      : invoicesData?.project.totalWithTax == null
                        ? '—'
                        : formatCurrency(invoicesData.project.totalWithTax)}
                  </p>
                </CardContent>
              </Card>

              <Card className="py-3">
                <CardContent>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Déjà facturé</p>
                  <p data-testid="invoice-total-billed" className="mt-2 text-xl font-semibold">
                    {isInvoicesLoading ? (
                      '—'
                    ) : invoicesData?.project.totalInvoiced == null ? (
                      '—'
                    ) : (
                      <>
                        {formatCurrency(invoicesData.project.totalInvoiced)}
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          (
                          {invoicesData.project.percentageInvoiced == null
                            ? '—'
                            : `${Math.round(invoicesData.project.percentageInvoiced)}%`}
                          )
                        </span>
                      </>
                    )}
                  </p>
                  <div className="mt-3">
                    <Progress
                      value={
                        invoicesData?.project.percentageInvoiced == null
                          ? 0
                          : Math.round(invoicesData.project.percentageInvoiced)
                      }
                      className="h-2"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="py-3">
                <CardContent>
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">TVA</p>
                    <p className="text-xs text-muted-foreground">Facturé / total (TVAC)</p>
                  </div>
                  <div className="mt-2">
                    {invoicesData?.project.vatBreakdown && invoicesData.project.vatBreakdown.length > 0 ? (
                      <div className="space-y-2">
                        {invoicesData.project.vatBreakdown
                          .map((b) => ({
                            percent: normalizeVatPercent(b.vatPercent),
                            totalToPayWithTax: b.totalToPayWithTax,
                            totalAlreadyInvoicedWithTax: b.totalAlreadyInvoicedWithTax,
                          }))
                          .filter((b) => b.percent != null)
                          .sort((a, b) => (a.percent as number) - (b.percent as number))
                          .map((b) => (
                            <div key={b.percent as number} className="flex items-start justify-between gap-3">
                              <Badge variant="outline" className="rounded-sm shrink-0">
                                {b.percent}%
                              </Badge>
                              <div className="flex-1 text-right">
                                <div className="text-sm font-medium">
                                  {formatCurrency(b.totalAlreadyInvoicedWithTax)} / {formatCurrency(b.totalToPayWithTax)}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : availableVatPercents.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        {availableVatPercents.map((v) => (
                          <Badge key={v} variant="outline" className="rounded-sm">
                            {v}%
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="py-3">
                <CardContent>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Reste à facturer</p>
                  <p
                    data-testid="invoice-remaining-balance"
                    className={cn(
                      'mt-2 text-xl font-semibold',
                      !isInvoicesLoading && nothingLeftToInvoice && 'text-muted-foreground',
                    )}
                  >
                    {isInvoicesLoading
                      ? '—'
                      : remainingBalance == null
                        ? '—'
                        : formatCurrency(remainingBalance)}
                  </p>
                  {/* The amount stays (AC-1): « 0,00 € » is the fact, the label below is what it
                      means. Keeping both also keeps every existing money assertion on the amount
                      valid — the label is a sibling, not a replacement. */}
                  {nothingLeftToInvoice && (
                    /* SOLY-REGIE-002 — trois situations, trois phrases, et l'ordre compte.
                       La décision d'abord (elle a été prise, elle prime sur tout constat), puis le
                       fait daté qui appelle un geste, puis les deux phrases existantes — dont
                       « Rien à facturer », qui reste MOT POUR MOT celle de la vente annulée. */
                    invoicingSummaryState === 'decided' ? (
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
                           data-testid="invoice-free-touch-up-decided">
                        {/* La forme du métré — « quoi, quand, qui, et comment revenir ». La QA du
                            20/08 l'a nommée comme le meilleur micro-texte du parcours. */}
                        <span>
                          Retouche gratuite — décidé le {formatDate(freeTouchUpDecidedAt ?? undefined)}
                          {interventionPricing?.notBilledByName
                            ? ` par ${interventionPricing.notBilledByName}`
                            : ''}
                        </span>
                        <span aria-hidden="true">·</span>
                        <DisabledHint hint={reasonIfDenied(PERMISSIONS.INVOICE_WRITE)}>
                          <button
                            type="button"
                            data-testid="invoice-free-touch-up-undo"
                            className="underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline"
                            disabled={isDecisionPending || !can(PERMISSIONS.INVOICE_WRITE)}
                            onClick={() => id && clearNotBilled(id)}
                          >
                            Annuler
                          </button>
                        </DisabledHint>
                      </div>
                    ) : invoicingSummaryState === 'to-price' ? (
                      <div className="mt-1 space-y-2">
                        <p className="text-xs text-muted-foreground" data-testid="invoice-work-done-unpriced">
                          Travail effectué le {formatDate(interventionPricing!.workCompletedOn!)} — prix à définir
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          {/* Le title va sur le wrapper, jamais sur le Button : la classe de base
                              porte `disabled:pointer-events-none`, donc un bouton grisé ne reçoit
                              aucun survol et resterait muet. C'était le défaut 🔴-2 de la QA du
                              20/08 — on ne le réintroduit pas. */}
                          <DisabledHint hint={reasonIfDenied(PERMISSIONS.INVOICE_WRITE)}>
                            <Button
                              size="sm"
                              data-testid="invoice-define-price-btn"
                              disabled={!can(PERMISSIONS.INVOICE_WRITE)}
                              onClick={() => navigate(`/projects/${id}/commande-directe`)}
                            >
                              Définir le prix
                            </Button>
                          </DisabledHint>
                          {/* Lien discret et non bouton : c'est la sortie de ~3 % des dossiers,
                              elle ne doit pas avoir le poids du geste dominant. */}
                          <DisabledHint hint={reasonIfDenied(PERMISSIONS.INVOICE_WRITE)}>
                            <button
                              type="button"
                              data-testid="invoice-free-touch-up-btn"
                              className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:cursor-not-allowed disabled:no-underline"
                              disabled={isDecisionPending || !can(PERMISSIONS.INVOICE_WRITE)}
                              onClick={() => setIsFreeTouchUpOpen(true)}
                            >
                              Retouche gratuite
                            </button>
                          </DisabledHint>
                        </div>
                      </div>
                    ) : invoicingSummaryState === 'not-priced-yet' ? (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="invoice-amount-not-set">
                        Montant pas encore défini
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-muted-foreground" data-testid="invoice-nothing-to-invoice">
                        Rien à facturer
                      </p>
                    )
                  )}
                  {decisionError && (
                    <p className="mt-2 text-xs text-destructive" data-testid="invoice-decision-error">
                      {decisionError}
                    </p>
                  )}
                </CardContent>
              </Card>

              <FreeTouchUpDialog
                open={isFreeTouchUpOpen}
                onOpenChange={setIsFreeTouchUpOpen}
                isPending={isDecisionPending}
                projectName={project?.name}
                onConfirm={() => id && markNotBilled(id)}
              />
            </div>

            {invoicesData?.paymentSchedule && (
              <Card className="py-3" data-testid="payment-schedule" data-settled={nothingLeftToInvoice ? 'true' : undefined}>
                <CardContent className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Échéances</p>
                  {nothingLeftToInvoice && (
                    // Neutral on purpose: the most common way to land here is a project invoiced to
                    // the last cent. Wording that reads as a failure ("ne s'applique plus") would
                    // brand every happy-path completion as a problem. « Soldé » is true whether the
                    // balance closed by invoicing, by credit note or by a cancelled sale.
                    <p className="text-xs text-muted-foreground -mt-2 mb-3" data-testid="payment-schedule-settled">
                      Échéancier soldé.
                    </p>
                  )}
                  {invoicesData.paymentSchedule.steps
                    // AC-2 — once the balance is settled the schedule must stop soliciting an
                    // invoice: keep what was actually invoiced (the history) and drop the steps
                    // still waiting, which no longer correspond to anything owed.
                    .filter((step) => !nothingLeftToInvoice || step.status === 'Invoiced' || step.invoicedAmount > 0)
                    .map((step) => {
                    const statusMap = {
                      Invoiced: 'invoiced', Due: 'due', Overdue: 'overdue',
                      Scheduled: 'scheduled', DateToBePlanned: 'unscheduled'
                    } as const
                    const dataStatus = statusMap[step.status] ?? 'unscheduled'
                    const icon = step.status === 'Invoiced' ? '✅'
                      : step.status === 'Overdue' ? '🔴'
                      : step.status === 'Scheduled' ? '📅'
                      : step.status === 'DateToBePlanned' ? '🔘'
                      : '⏳' // Due

                    const stepLabel = step.invoiceType === 'final' ? 'Solde' : 'Acompte'
                    // Nothing left to invoice on the project as a whole ⇒ no step can be actioned,
                    // whatever its own status still says.
                    const isActionable = !nothingLeftToInvoice
                      && (step.status === 'Due' || step.status === 'Overdue')
                      && step.remainingAmount > 0
                    const hasPartialCredit = step.invoicedAmount > 0 && step.status !== 'Invoiced'
                    const rolloverAmount = step.remainingAmount > step.suggestedAmount
                      ? step.remainingAmount - step.suggestedAmount
                      : 0

                    const handleFacturer = () => {
                      const params = new URLSearchParams({
                        step: '2',
                        invoice_type: step.invoiceType,
                        amount: String(step.remainingAmount),
                      })
                      navigate(`/projets/${id}/invoices/new?${params.toString()}`)
                    }

                    return (
                      <div key={step.id} className="py-1.5" data-testid="schedule-step">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span data-testid="schedule-step-status" data-status={dataStatus}>{icon}</span>
                            <span className={step.status === 'Invoiced' ? 'text-muted-foreground line-through' : 'font-medium'}>
                              {stepLabel}
                            </span>
                            <Badge variant="outline" className="rounded-sm text-xs">{step.percentage}%</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span data-testid="schedule-step-amount" className={step.status === 'Invoiced' ? 'text-muted-foreground' : 'font-medium'}>
                              {step.remainingAmount > 0 ? formatCurrency(step.remainingAmount) : '—'}
                            </span>
                            {isActionable && allowCreateInvoices && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs"
                                onClick={handleFacturer}
                                data-testid="schedule-step-invoice-btn"
                              >
                                Facturer
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Secondary info line */}
                        {step.status === 'Overdue' && (
                          <p className="text-xs text-red-500 mt-0.5 ml-6">
                            En retard · {step.daysOverdue} j.ouv.
                            {hasPartialCredit && (
                              <span className="text-muted-foreground"> · sur {formatCurrency(step.suggestedAmount)} prévu, {formatCurrency(step.invoicedAmount)} déjà avancé</span>
                            )}
                            {rolloverAmount > 0 && (
                              <span className="text-muted-foreground"> · étape {formatCurrency(step.suggestedAmount)} + {formatCurrency(rolloverAmount)} de rattrapage</span>
                            )}
                          </p>
                        )}
                        {step.status === 'Due' && step.triggerDate && (
                          <p className="text-xs text-orange-500 mt-0.5 ml-6">
                            À facturer · éch. {formatDate(step.triggerDate)}
                            {hasPartialCredit && (
                              <span className="text-muted-foreground"> · sur {formatCurrency(step.suggestedAmount)} prévu, {formatCurrency(step.invoicedAmount)} déjà avancé</span>
                            )}
                            {rolloverAmount > 0 && (
                              <span className="text-muted-foreground"> · étape {formatCurrency(step.suggestedAmount)} + {formatCurrency(rolloverAmount)} de rattrapage</span>
                            )}
                          </p>
                        )}
                        {step.status === 'Scheduled' && step.triggerDate && (
                          <p className="text-xs text-muted-foreground mt-0.5 ml-6" title={formatDate(step.triggerDate)}>
                            {formatRelativeDate(step.triggerDate)}
                            {hasPartialCredit && (
                              <span> · sur {formatCurrency(step.suggestedAmount)} prévu, {formatCurrency(step.invoicedAmount)} déjà avancé</span>
                            )}
                            {rolloverAmount > 0 && (
                              <span> · étape {formatCurrency(step.suggestedAmount)} + {formatCurrency(rolloverAmount)} de rattrapage</span>
                            )}
                          </p>
                        )}

                        {/* Partial credit for steps without a secondary time line (DateToBePlanned) */}
                        {hasPartialCredit && step.status === 'DateToBePlanned' && (
                          <p className="text-xs text-muted-foreground mt-0.5 ml-6">
                            sur {formatCurrency(step.suggestedAmount)} prévu · {formatCurrency(step.invoicedAmount)} déjà avancé
                          </p>
                        )}
                      </div>
                    )
                  })}
                </CardContent>
              </Card>
            )}

            <Card className="py-3">
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Factures ({sortedInvoices.length})
                  </p>
                  <div className="mt-2 rounded-lg border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="py-1.5">Référence</TableHead>
                          <TableHead className="py-1.5">Statut</TableHead>
                          <TableHead className="py-1.5">Objet</TableHead>
                          <TableHead className="py-1.5 text-right">Total TVAC</TableHead>
                          <TableHead className="py-1.5 text-right">TVA</TableHead>
                          <TableHead className="py-1.5 text-right">Date</TableHead>
                          <TableHead className="py-1.5 text-right">Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isInvoicesLoading ? (
                          <TableRow>
                            <TableCell colSpan={7} className="py-4 text-center text-sm text-muted-foreground">
                              Chargement…
                            </TableCell>
                          </TableRow>
                        ) : sortedInvoices.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="py-4 text-center text-sm text-muted-foreground">
                              Aucune facture
                            </TableCell>
                          </TableRow>
                        ) : (
                          sortedInvoices.map((inv) => (
                            <TableRow key={inv.id} data-testid="invoice-row">
                              <TableCell className="py-1.5 font-medium">
                                <span data-testid="invoice-row-number">{inv.number}</span>
                                <InvoiceStatusBadge
                                  isSuperseded={inv.isSuperseded}
                                  supersededByInvoiceId={inv.supersededByInvoiceId}
                                  supersededAt={inv.supersededAt}
                                />
                              </TableCell>
                              <TableCell className="py-1.5">{getInvoiceStatusBadgeV2(inv.status)}</TableCell>
                              <TableCell className={`py-1.5 ${inv.type === 'credit_note' ? 'text-red-600' : 'text-muted-foreground'}`}>{getInvoiceTypeLabel(inv.type)}</TableCell>
                              <TableCell className={`py-1.5 text-right font-semibold ${inv.type === 'credit_note' ? 'text-red-600' : ''}`}>
                                {inv.type === 'credit_note' ? `- ${formatCurrency(Math.abs(inv.amountWithTax))}` : formatCurrency(inv.amountWithTax)}
                              </TableCell>
                              <TableCell className="py-1.5 text-right">
                                {(() => {
                                  const percents = (inv.vatPercents || [])
                                    .map((v) => normalizeVatPercent(v))
                                    .filter((v): v is number => v != null)
                                    .filter((v, idx, arr) => arr.indexOf(v) === idx)
                                    .sort((a, b) => a - b)

                                  const fallbackPercents = availableVatPercents

                                  const toRender = percents.length > 0 ? percents : fallbackPercents

                                  if (toRender.length === 0) {
                                    return <span className="text-sm text-muted-foreground">—</span>
                                  }

                                  return (
                                    <div className="flex justify-end gap-1.5 flex-wrap">
                                      {toRender.map((v) => (
                                        <Badge key={v} variant="outline" className="rounded-sm">
                                          {v}%
                                        </Badge>
                                      ))}
                                    </div>
                                  )
                                })()}
                              </TableCell>
                              <TableCell className="py-1.5 text-right text-muted-foreground">
                                {formatDate(inv.invoiceDate, 'd MMM yyyy')}
                              </TableCell>
                              <TableCell className="py-1.5 text-right">
                                <InvoiceRowActions
                                  invoice={inv}
                                  onPreview={() => handleOpenInvoicePdf(inv.id)}
                                  onDelete={() => setDeleteInvoiceTarget(inv)}
                                />
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
            </>
          ) : null}

          <Separator />

          <section className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">Exécution</h2>
                <p className="text-sm text-muted-foreground">Suivi chantier, livraison, documents et métrage</p>
              </div>
            </div>

            <ProjectDocumentsSection projectId={id} />

            <Card className="py-3">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-base">Détails chantier</CardTitle>
                    <p className="text-xs text-muted-foreground">Informations utiles pour la planification.</p>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* (2026-07-07) "Générer bordereau" from the accepted order hidden:
                        404s when the quote isn't accepted yet — users generate from the
                        quotes instead (Devis section → WorkOrderSelectionDialog). */}
                    {canEdit ? (
                      <Button size="sm" variant="outline" data-testid="project-execution-edit-btn" onClick={() => setIsExecutionDialogOpen(true)}>
                        <Pencil className="h-4 w-4" />
                        Modifier
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {isExecutionLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : executionError ? (
                  <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-muted-foreground">
                    {executionError}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-lg border bg-background py-1.5 px-3">
                    <div className="text-xs text-muted-foreground">Date souhaitée</div>
                    <div className="mt-1 text-sm font-medium">
                      {formatDateOnly(executionDetails?.desiredInstallationDate)}
                    </div>
                  </div>

                  <div className="rounded-lg border bg-background py-1.5 px-3">
                    <div className="text-xs text-muted-foreground">Étage</div>
                    <div className="mt-1 text-sm font-medium">
                      {executionDetails?.floorKind === 1
                        ? executionDetails?.floorNumber != null
                          ? String(executionDetails.floorNumber)
                          : '—'
                        : floorKindLabel(executionDetails?.floorKind ?? null)}
                    </div>
                  </div>

                  <div className="rounded-lg border bg-background py-1.5 px-3">
                    <div className="text-xs text-muted-foreground">Ascenseur</div>
                    <div className="mt-1 text-sm font-medium">{triStateLabel(executionDetails?.hasElevator ?? null)}</div>
                  </div>

                  <div className="rounded-lg border bg-background py-1.5 px-3">
                    <div className="text-xs text-muted-foreground">Besoin lift</div>
                    <div className="mt-1 text-sm font-medium">{triStateLabel(executionDetails?.needsLift ?? null)}</div>
                  </div>

                  <div className="rounded-lg border bg-background py-1.5 px-3">
                    <div className="text-xs text-muted-foreground">Besoin métrés</div>
                    <div className="mt-1 text-sm font-medium">{triStateLabel(executionDetails?.needsMeasurements ?? null)}</div>
                  </div>

                  <div className="rounded-lg border bg-background py-1.5 px-3 md:col-span-2">
                    <div className="text-xs text-muted-foreground">Note placeurs</div>
                    <div className="mt-2 text-sm text-muted-foreground whitespace-pre-wrap">
                      {executionDetails?.installersNote ? executionDetails.installersNote : '—'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <Dialog open={isExecutionDialogOpen} onOpenChange={setIsExecutionDialogOpen}>
            <DialogContent className="sm:max-w-[560px]">
              <DialogHeader>
                <DialogTitle>Détails chantier</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Date souhaitée d'installation</Label>
                  <Input
                    type="date"
                    data-testid="project-execution-desired-date-input"
                    value={desiredInstallationDateDraft}
                    onChange={(e) => setDesiredInstallationDateDraft(e.target.value)}
                    disabled={isSavingExecution}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type d'étage</Label>
                    <Select
                      value={floorKindDraft == null ? 'unset' : String(floorKindDraft)}
                      onValueChange={(v) => {
                        if (v === 'unset') {
                          setFloorKindDraft(null)
                          setFloorNumberDraft('')
                          return
                        }
                        const n = Number(v)
                        const next = Number.isFinite(n) ? (n as FloorKind) : null
                        setFloorKindDraft(next)
                        if (next !== 1) {
                          setFloorNumberDraft('')
                        }
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="À préciser" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">À préciser</SelectItem>
                        <SelectItem value="0">Rez-de-chaussée</SelectItem>
                        <SelectItem value="1">Étage numéroté</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {floorKindDraft === 1 ? (
                    <div className="space-y-2">
                      <Label>Numéro d'étage</Label>
                      <Input
                        type="number"
                        value={floorNumberDraft}
                        onChange={(e) => setFloorNumberDraft(e.target.value)}
                        disabled={isSavingExecution}
                        placeholder="Ex: 3"
                      />
                    </div>
                  ) : (
                    <div />
                  )}
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Ascenseur</Label>
                    <Select value={triStateValue(hasElevatorDraft)} onValueChange={(v) => setHasElevatorDraft(parseTriState(v))}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="À préciser" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">À préciser</SelectItem>
                        <SelectItem value="yes">Oui</SelectItem>
                        <SelectItem value="no">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Besoin lift</Label>
                    <Select value={triStateValue(needsLiftDraft)} onValueChange={(v) => setNeedsLiftDraft(parseTriState(v))}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="À préciser" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">À préciser</SelectItem>
                        <SelectItem value="yes">Oui</SelectItem>
                        <SelectItem value="no">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Besoin métrés</Label>
                    <Select value={triStateValue(needsMeasurementsDraft)} onValueChange={(v) => setNeedsMeasurementsDraft(parseTriState(v))}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="À préciser" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unset">À préciser</SelectItem>
                        <SelectItem value="yes">Oui</SelectItem>
                        <SelectItem value="no">Non</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Note placeurs</Label>
                  <Textarea
                    value={installersNoteDraft}
                    onChange={(e) => setInstallersNoteDraft(e.target.value)}
                    disabled={isSavingExecution}
                    rows={4}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsExecutionDialogOpen(false)} disabled={isSavingExecution}>
                  Annuler
                </Button>
                <Button type="button" data-testid="project-execution-save-btn" onClick={handleSaveExecutionDetails} disabled={isSavingExecution || !canEdit}>
                  {isSavingExecution ? 'Enregistrement...' : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </main>
    </div>
  )
}
